import moment from 'moment';
import { isKpiTrendsHourlyGranularity } from '../hourSegmentUtils';
import { TMetricValueList } from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList';
import { fillInEmptyData } from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList/hooks/utils';
import { getStdPlatform } from '@intelligence/components/DownloadCommon/DownloadPage/common/dataSettingAtom';
import { getCountryNameByCountryCode } from '@intelligence/utils/commonUtils';
import type { IRegion } from '@intelligence/types/common';

// 删除 compare 接口无关的参数
const deleteIrrelevantParameters = (params: { [key: string]: any }) => {
  if (!params) return;
  [
    'unified_ids',
    'regions',
    'date_type',
    'segment_list',
    'device',
    'data_method',
  ].forEach((key) => {
    // eslint-disable-next-line no-param-reassign
    delete params[key];
  });
};

// compare 接口获取表格及普通折线图数据，参数适配
export const formatParams = (fetchParams: any) => {
  const isHourly = isKpiTrendsHourlyGranularity(fetchParams?.date_type);

  if (isHourly) {
    const hourlyParams: Record<string, any> = {
      entity_type: fetchParams.entity_type,
      ids: fetchParams.unified_ids ?? fetchParams.ids,
      granularity: fetchParams.date_type,
      source_metrics: fetchParams.source_metrics,
      start_date: fetchParams.start_date || '',
      end_date: fetchParams.end_date || '',
    };

    return hourlyParams;
  }

  const formattedParams = {
    ...fetchParams,
    ids: fetchParams.unified_ids,
    markets: fetchParams.regions,
    granularity: fetchParams.date_type,
    segments: fetchParams.segment_list,
    platforms: fetchParams.device,
    b_true_data: fetchParams?.b_true_data ?? true,
    // 获取表格和普通折线图数据，按时间拆分数据、表格带环比数据、sum_avg 数据
    b_group_by_date: true,
    b_growth_ratio: true,
    b_sum_avg_show: true,
    // 以下为默认传参（b_group_by_market 允许外部覆盖）
    b_group_by_market: fetchParams?.b_group_by_market ?? false,
    b_group_by_region: false,
    b_group_by_platform: false,
    b_group_by_segment: false,
    start_release_time: 0,
    end_release_time: 0,
  } as any;

  if (formattedParams.source_metrics && typeof formattedParams.source_metrics === 'object') {
    formattedParams.source_metrics = Object.keys(formattedParams.source_metrics).reduce(
      (acc: any, key: string) => {
        const list = formattedParams.source_metrics[key];
        acc[key] = Array.isArray(list) ? list : [];
        return acc;
      },
      {},
    );
  }

  deleteIrrelevantParameters(formattedParams);

  return formattedParams;
};

// compare 接口获取 ByPlatform 折线图 及饼图数据数据，参数适配
export const formatPlatformPieChartParams = ({
  fetchParams,
  isByPlatform,
  isPie,
  isByCountry,
}: {
  fetchParams: any;
  isByPlatform?: boolean;
  isPie?: boolean;
  isByCountry?: boolean;
}) => {
  const formattedParams = {
    ...fetchParams,
    ids: fetchParams.unified_ids,
    markets: fetchParams.regions,
    granularity: fetchParams.date_type,
    segments: fetchParams.segment_list,
    platforms: fetchParams.device,
    // 默认传真实数据标识，允许外部显式覆盖
    b_true_data: fetchParams?.b_true_data ?? true,
    // 不是饼图时获取按时间拆分数据
    b_group_by_date: !isPie,
    // 勾选「按平台」时按 platform 维度拆分，不再传 b_group_by_device
    b_group_by_platform: !!isByPlatform,
    // 以下为默认传参
    b_growth_ratio: false,
    // 按国家饼图时需要按 market 分组，后端才返回各国家的数据
    b_group_by_market: !!(isByCountry && isPie),
    b_group_by_region: false,
    b_group_by_segment: false,
    start_release_time: 0,
    end_release_time: 0,
  };

  deleteIrrelevantParameters(formattedParams);

  return formattedParams;
};

interface ITrendsDataItem {
  date: string;
  metrics: {
    growth_ratio: number;
    metric: string;
    name: string;
    value: number;
  }[];
}
type TTrendsData = ITrendsDataItem[];

interface IFormatTrendsDataProps {
  data: any;
  dateType: 'daily' | 'weekly' | 'monthly' | 'hourly';
  responseQueryDate: { startDate: string; endDate: string };
}
/**
 * 处理折线图数据为当前页面需要的数据
 */
export const formatTrendsData = ({
  data,
  dateType,
  responseQueryDate,
}: IFormatTrendsDataProps): {
  trends: TTrendsData;
  sum_avg: any;
} => {
  const currentMetricsData = data?.[0]?.metric_value_list || [];
  const sumAvg = data?.[0]?.sum_avg || [];
  const metricDataMap: { [key: string]: any } = {};

  currentMetricsData.forEach((item: any) => {
    // When source is truedata, align the record date to the current granularity bucket
    // so it will match generated ticks from fillInEmptyData and won't be dropped.
    let dateKey = item?.date;
    if (item?.source === 'truedata' && item?.date) {
      if (dateType === 'weekly') {
        dateKey = moment(item.date).startOf('week').format('YYYY-MM-DD');
      } else if (dateType === 'monthly') {
        dateKey = moment(item.date).startOf('month').format('YYYY-MM-DD');
      }
    }

    // Do not mutate the original item's date; only use dateKey for grouping.
    const pushItem = { ...item, name: item.metric };

    if (metricDataMap[dateKey]) {
      metricDataMap[dateKey].push(pushItem);
    } else {
      metricDataMap[dateKey] = [pushItem];
    }
  });
  const trends = Object.entries(metricDataMap)
    .map(([key, value]) => {
      return {
        date: key,
        metrics: value,
      };
    })
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

  // 空数据补齐（hourly 由后端返回完整小时序列，不做日/周/月补空）
  const filledTrends =
    dateType === 'hourly'
      ? trends
      : fillInEmptyData({
          currentMetricData: trends as unknown as TMetricValueList,
          dateType,
          responseQueryDate,
        });

  return {
    trends: filledTrends as ITrendsDataItem[],
    sum_avg: sumAvg,
  };
};

/**
 * 处理 byPlatform 的饼图数据、普通饼图数据、byPlatform 的折线图数据
 */
export const formatPlatformPieData = ({
  data,
  isPie,
  isByPlatform,
  dateType,
  responseQueryDate,
}: {
  data: any;
  isPie?: boolean;
  isByPlatform?: boolean;
  dateType: 'daily' | 'weekly' | 'monthly';
  responseQueryDate: { startDate: string; endDate: string };
}): {
  pieDataByPlatform?: any;
  trendsDataByPlatform?: any;
  pieData?: any;
} => {
  const currentMetricsData = data?.[0]?.metric_value_list || [];

  // 饼图 && ByPlatform
  if (isPie && isByPlatform) {
    const platformMetricMap: { [key: string]: any } = {};
    currentMetricsData.forEach((item: any) => {
      const platformValue = getStdPlatform(item?.platform, item?.device) || '';
      const currentItem = { ...item, device: platformValue };

      if (platformMetricMap[platformValue]) {
        platformMetricMap[platformValue].push(currentItem);
      } else {
        platformMetricMap[platformValue] = [currentItem];
      }
    });

    return {
      pieDataByPlatform: platformMetricMap,
    };
  }

  // 饼图
  if (isPie) {
    return {
      pieData: currentMetricsData,
    };
  }

  // 折线图 && ByPlatform
  const metricDataMap: { [key: string]: any } = {};
  currentMetricsData.forEach((item: any) => {
    if (metricDataMap[item?.date]) {
      metricDataMap[item?.date].push({ ...item, name: item.metric });
    } else {
      metricDataMap[item?.date] = [{ ...item, name: item.metric }];
    }
  });

  const trends = Object.entries(metricDataMap)
    .map(([key, value]) => {
      const metricMap: { [key: string]: any } = {};
      value.forEach((item: any) => {
        const platformValue = getStdPlatform(item?.platform, item?.device) || '';

        if (platformValue) {
          if (metricMap[platformValue]) {
            metricMap[platformValue].push(item);
          } else {
            metricMap[platformValue] = [item];
          }
        }
      });
      return {
        date: key,
        metrics: metricMap,
      };
    })
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

  // 空数据补齐
  const filledTrends = fillInEmptyData({
    currentMetricData: trends as unknown as TMetricValueList,
    dateType,
    responseQueryDate,
  });

  return {
    trendsDataByPlatform: filledTrends,
  };
};

/**
 * 处理 By Country 数据
 * 后端返回的 metric_value_list 每条记录带 market 字段（国家代码）
 * 1. 先按 date + market 分组 → 生成与 stackLineData 相同的格式（折线图按国家展示）
 * 2. 跨所有日期汇总每个国家的 value，取 Top5 国家，其余归 Others
 *
 * 返回格式与 formatPlatformPieData 的 trendsDataByPlatform 一致：
 * [{ date: string, metrics: { [country: string]: Array<{metric, value, ...}> } }]
 */
const TOP_COUNTRY_COUNT = 10;

export const formatByCountryData = ({
  data,
  dateType,
  responseQueryDate,
  regionsCountries,
}: {
  data: any;
  dateType: 'daily' | 'weekly' | 'monthly';
  responseQueryDate: { startDate: string; endDate: string };
  regionsCountries?: IRegion[];
}): {
  trends: any[];
  sum_avg: any;
  countryTrendsData: any[];
} => {
  const currentMetricsData = data?.[0]?.metric_value_list || [];
  const sumAvg = data?.[0]?.sum_avg || [];

  // ========== 第一步：生成普通表格数据（按日期分组，同 formatTrendsData） ==========
  const metricDataMap: { [key: string]: any } = {};
  currentMetricsData.forEach((item: any) => {
    let dateKey = item?.date;
    if (item?.source === 'truedata' && item?.date) {
      if (dateType === 'weekly') {
        dateKey = moment(item.date).startOf('week').format('YYYY-MM-DD');
      } else if (dateType === 'monthly') {
        dateKey = moment(item.date).startOf('month').format('YYYY-MM-DD');
      }
    }
    const pushItem = { ...item, name: item.metric };
    if (metricDataMap[dateKey]) {
      metricDataMap[dateKey].push(pushItem);
    } else {
      metricDataMap[dateKey] = [pushItem];
    }
  });

  // 表格数据：按日期分组后，同一日期同一 metric 的各国家数据合并（value 相加）
  const trendsForTable = Object.entries(metricDataMap)
    .map(([dateKey, items]: [string, any[]]) => {
      // 按 metric 聚合，将同一 metric 的所有国家的 value 求和
      const metricAggMap: { [metric: string]: any } = {};
      items.forEach((item: any) => {
        if (metricAggMap[item.metric]) {
          metricAggMap[item.metric].value += item.value || 0;
        } else {
          metricAggMap[item.metric] = { ...item, value: item.value || 0 };
        }
      });
      return {
        date: dateKey,
        metrics: Object.values(metricAggMap),
      };
    })
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

  const filledTrendsForTable = fillInEmptyData({
    currentMetricData: trendsForTable as unknown as TMetricValueList,
    dateType,
    responseQueryDate,
  });

  // ========== 第二步：计算 Top5 国家 ==========
  // 跨所有日期汇总每个国家的总 value
  const countryTotalMap: { [market: string]: number } = {};
  currentMetricsData.forEach((item: any) => {
    const market = item?.market;
    if (market) {
      countryTotalMap[market] = (countryTotalMap[market] || 0) + (item.value || 0);
    }
  });

  // 按总 value 降序排列，取 Top5
  const sortedCountries = Object.entries(countryTotalMap)
    .sort(([, a], [, b]) => b - a);
  const topCountries = sortedCountries.slice(0, TOP_COUNTRY_COUNT).map(([code]) => code);
  const hasOthers = sortedCountries.length > TOP_COUNTRY_COUNT;

  // ========== 第三步：构建国家代码 → 国际化名称的映射 ==========
  // 将国家代码（如 'de'）转为国际化名称（如 '德国' / 'Germany'），折线图 series 名使用国际化名称
  const countryCodeToName: { [code: string]: string } = {};
  topCountries.forEach((code) => {
    countryCodeToName[code] = regionsCountries
      ? getCountryNameByCountryCode(regionsCountries, code)
      : code;
  });

  // ========== 第四步：生成折线图数据（stackLineData 格式，按国家分组） ==========
  const countryMetricDataMap: { [date: string]: any[] } = {};
  currentMetricsData.forEach((item: any) => {
    let dateKey = item?.date;
    if (item?.source === 'truedata' && item?.date) {
      if (dateType === 'weekly') {
        dateKey = moment(item.date).startOf('week').format('YYYY-MM-DD');
      } else if (dateType === 'monthly') {
        dateKey = moment(item.date).startOf('month').format('YYYY-MM-DD');
      }
    }
    if (countryMetricDataMap[dateKey]) {
      countryMetricDataMap[dateKey].push({ ...item, name: item.metric });
    } else {
      countryMetricDataMap[dateKey] = [{ ...item, name: item.metric }];
    }
  });

  const countryTrends = Object.entries(countryMetricDataMap)
    .map(([dateKey, items]: [string, any[]]) => {
      // 按国家分组，非 Top5 的归入 Others；key 使用国际化名称
      const countryMap: { [country: string]: any[] } = {};

      items.forEach((item: any) => {
        const market = item?.market;
        const isTopCountry = topCountries.includes(market);
        // 使用国际化名称作为 key，非 Top5 归为 Others
        const countryKey = isTopCountry
          ? (countryCodeToName[market] || market)
          : (hasOthers ? 'Others' : (countryCodeToName[market] || market));

        if (!countryMap[countryKey]) {
          countryMap[countryKey] = [];
        }

        if (countryKey === 'Others') {
          // Others 需要按 metric 聚合同一日期内不同国家的 value
          const existingMetric = countryMap[countryKey].find(
            (m: any) => m.metric === item.metric,
          );
          if (existingMetric) {
            existingMetric.value += item.value || 0;
          } else {
            countryMap[countryKey].push({
              ...item,
              market: 'Others',
              value: item.value || 0,
            });
          }
        } else {
          countryMap[countryKey].push(item);
        }
      });

      return {
        date: dateKey,
        metrics: countryMap,
      };
    })
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

  // 空数据补齐
  const filledCountryTrends = fillInEmptyData({
    currentMetricData: countryTrends as unknown as TMetricValueList,
    dateType,
    responseQueryDate,
  });

  return {
    trends: filledTrendsForTable as any[],
    sum_avg: sumAvg,
    countryTrendsData: filledCountryTrends as any[],
  };
};
